/**
 * Dialog para criar nova regra de roteamento
 * 
 * Suporta condições compostas (AND/OR) e sequência de ações
 * usando RoutingActionBuilder (similar ao CampaignMessageBuilder)
 * Permite selecionar inboxes específicas ou aplicar a todas.
 * 
 * @version 2.1.0
 * @date 2025-12-21
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CompositeConditionBuilder, 
  CompositeConditionConfig,
  createEmptyCompositeConfig,
} from './CompositeConditionBuilder';
import { RoutingActionBuilder } from './RoutingActionBuilder';
import { RoutingActionItem } from './types';
import { InboxSelector } from './InboxSelector';
import { WebhookConditionConfig } from './WebhookConditionConfig';
import { createLogger } from '@/lib/logger';
const routingLogger = createLogger('CreateRuleDialog');

// Interface para mapeamento de campos do webhook
interface FieldMapping {
  contact_phone: string;
  contact_name: string;
}

interface CreateRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateRuleDialog({ open, onOpenChange, onSuccess }: CreateRuleDialogProps) {
  const { currentTenant, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Campos do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [conditionConfig, setConditionConfig] = useState<CompositeConditionConfig>(
    createEmptyCompositeConfig()
  );
  const [actions, setActions] = useState<RoutingActionItem[]>([]);
  const [stopOnMatch, setStopOnMatch] = useState(true);
  
  // Estados para seleção de inboxes
  const [allInboxes, setAllInboxes] = useState(true);
  const [selectedInboxIds, setSelectedInboxIds] = useState<string[]>([]);
  
  // Estado para mapeamento de campos do webhook
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({
    contact_phone: 'telefone',
    contact_name: 'nome',
  });
  
  // Verifica se alguma condição é do tipo webhook
  const hasWebhookCondition = conditionConfig.conditions.some(c => c.type === 'webhook');

  // Reset form para estado inicial
  const resetForm = () => {
    setName('');
    setDescription('');
    setConditionConfig(createEmptyCompositeConfig());
    setActions([]);
    setStopOnMatch(true);
    setAllInboxes(true);
    setSelectedInboxIds([]);
    setFieldMapping({
      contact_phone: 'telefone',
      contact_name: 'nome',
    });
  };

  // Validar condição
  const validateCondition = (cond: CompositeConditionConfig['conditions'][0]): boolean => {
    switch (cond.type) {
      case 'keyword':
        const keywords = (cond.value.keywords as string[]) || [];
        return keywords.length > 0;
      case 'regex':
        return !!(cond.value.pattern as string);
      case 'time_range':
        return !!(cond.value.start && cond.value.end);
      case 'first_message':
        return true; // Sempre válido - nova conversa
      case 'manual_execution':
        return true; // Sempre válido - execução manual não requer campos adicionais
      case 'webhook':
        return true; // Sempre válido - webhook não requer campos adicionais na condição
      case 'contact_tag':
        const tags = (cond.value.tag_ids as string[]) || [];
        return tags.length > 0;
      default:
        return false;
    }
  };

  // Criar regra
  const handleSubmit = async () => {
    if (!currentTenant?.id) return;

    // Validações básicas
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    // Validação de inboxes
    if (!allInboxes && selectedInboxIds.length === 0) {
      toast.error('Selecione pelo menos uma inbox ou marque "Todas as inboxes"');
      return;
    }

    // Validação de condições (todas devem ser válidas)
    const invalidConditions = conditionConfig.conditions.filter(c => !validateCondition(c));
    if (invalidConditions.length > 0) {
      toast.error('Preencha todas as condições corretamente');
      return;
    }

    // Validação de ações
    if (actions.length === 0) {
      toast.error('Adicione pelo menos uma ação');
      return;
    }

    setLoading(true);

    try {
      // Pega a primeira condição e ação para compatibilidade com sistema legado
      const firstCondition = conditionConfig.conditions[0];
      const firstAction = actions[0];

      // Cria regra + actions atomicamente via RPC server-side
      // (calcula próxima prioridade e invalida trigger_cache internamente)
      const { error: ruleError } = await supabase.rpc('create_routing_rule', {
        p_payload: {
          name: name.trim(),
          description: description.trim() || null,
          condition_type: firstCondition.type,
          condition_value: conditionConfig as unknown as Record<string, unknown>,
          action_type: firstAction.action_type,
          action_value: firstAction.action_value as Record<string, unknown>,
          stop_on_match: stopOnMatch,
          inbox_ids: allInboxes ? null : selectedInboxIds,
          field_mapping: hasWebhookCondition ? (fieldMapping as unknown as Record<string, unknown>) : null,
        } as any,
        p_actions: actions.map((action, index) => ({
          action_type: action.action_type,
          action_value: action.action_value,
          sort_order: index,
          delay_seconds: action.delay_seconds || 0,
        })) as any,
        p_tenant_hint: currentTenant.id,
      });

      if (ruleError) throw ruleError;

      routingLogger.info('Regra criada e cache de triggers invalidado');

      toast.success('Regra criada com sucesso');
      resetForm();
      onSuccess();
    } catch (error) {
      routingLogger.error('Erro ao criar regra', error);
      toast.error('Erro ao criar regra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Nova Macro</DialogTitle>
          <DialogDescription>
            Configure quando e como as açoes rápidas devem ser executadas automaticamente.
            Combine múltiplas condições usando E/OU.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-6 py-4">
            {/* Nome e Descrição */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Macro *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Rotear vendas em horário comercial"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrição opcional da regra..."
                  rows={2}
                />
              </div>
            </div>

            {/* Seleção de Inboxes */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Aplicar em (Inboxes)</h3>
              <p className="text-sm text-muted-foreground">
                Selecione em quais inboxes esta regra será aplicada.
              </p>
              <InboxSelector
                allInboxes={allInboxes}
                selectedInboxIds={selectedInboxIds}
                onAllInboxesChange={setAllInboxes}
                onSelectedIdsChange={setSelectedInboxIds}
                showValidation={false}
              />
            </div>

            {/* Condições Compostas */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Quando (Condições)</h3>
              <p className="text-sm text-muted-foreground">
                Configure uma ou mais condições. Use E/OU para combinar.
              </p>
              <CompositeConditionBuilder
                config={conditionConfig}
                onChange={setConditionConfig}
              />
            </div>

            {/* Configuração de Webhook (apenas se condição for webhook) */}
            {hasWebhookCondition && (
              <div className="space-y-4 p-4 border rounded-lg border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-950/20">
                <h3 className="font-medium text-rose-700 dark:text-rose-300">Configuração do Webhook</h3>
                <p className="text-sm text-rose-600 dark:text-rose-400">
                  Após criar a regra, você receberá uma URL única para configurar no sistema externo.
                </p>
                <WebhookConditionConfig
                  fieldMapping={fieldMapping}
                  onFieldMappingChange={setFieldMapping}
                  isCreating={true}
                />
              </div>
            )}

            {/* Sequência de Ações */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Fazer (Sequência de Ações)</h3>
              <RoutingActionBuilder
                items={actions}
                onItemsChange={setActions}
                selectedInboxIds={allInboxes ? [] : selectedInboxIds}
              />
            </div>

            {/* Opções */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="stop-on-match">Parar ao dar match</Label>
                <p className="text-sm text-muted-foreground">
                  Se ativado, não verifica as próximas regras quando esta der match
                </p>
              </div>
              <Switch
                id="stop-on-match"
                checked={stopOnMatch}
                onCheckedChange={setStopOnMatch}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 shrink-0 border-t mt-auto">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Criando...' : 'Criar Regra'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
